/**
 * BONQUERÉ — VERSE navigation focus reveal  (BNQ-WEB-0521 · Sprint 3.4.16)
 *
 * Twelve lines that close an accessibility gap CSS cannot reach.
 *
 * ── THE DEFECT ───────────────────────────────────────────────────────────────────────────────────
 * When a horizontally scrollable rail overflows, a link that is only PARTIALLY visible receives
 * keyboard focus without the browser scrolling it into view. Chrome's focus scrolling uses
 * `inline: nearest` semantics, and for an element whose nearest edge is already inside the
 * scrollport it performs NO scroll at all — so `scroll-margin-inline` on the link and
 * `scroll-padding-inline` on the container are never consulted. Both were measured applied at 24px
 * with the link still held 59px clipped at 360px wide, scrollLeft pinned at 0 across a full second
 * of sampling. That is the same failure as the open Shipping defect BNQ-WEB-0221, and it is why the
 * CSS-only mitigation there does not work either.
 *
 * ── WHY THIS FILE EXISTS AT ALL ─────────────────────────────────────────────────────────────────
 * The rail was designed to need no JavaScript, and everything else about it still does not: the
 * anchors are real links, the scroll affordance is `scroll-hint` plus a CSS scroll-timeline, and the
 * whole rail works with this file absent. This handles ONE case — revealing a focused link — because
 * a focus ring a keyboard user cannot see is a real barrier, and a comment claiming the CSS solved
 * it would have been wrong.
 *
 * ── WHAT IT DELIBERATELY DOES NOT DO ────────────────────────────────────────────────────────────
 * No observers, no polling, no resize handling, no scroll listener, no layout writes on scroll. One
 * delegated `focusin` handler that reads two rectangles and adjusts `scrollLeft` only when the
 * focused link is genuinely clipped. Scrolling is instant rather than smooth, so there is no motion
 * to gate behind `prefers-reduced-motion` — a focus jump should be immediate in any case.
 */
(() => {
  const PAD = 24; // matches scroll-padding-inline / scroll-margin-inline in the section stylesheet

  document.addEventListener(
    'focusin',
    (event) => {
      const link = event.target.closest?.('.bnq-verse-nav__link');
      if (!link) return;

      const scroller = link.closest('.bnq-verse-nav__scroller');
      if (!scroller || scroller.scrollWidth <= scroller.clientWidth) return;

      const linkBox = link.getBoundingClientRect();
      const viewBox = scroller.getBoundingClientRect();
      const pastEnd = linkBox.right + PAD - viewBox.right;
      const pastStart = viewBox.left + PAD - linkBox.left;

      // Minimal correction only, and only one direction can be true at a time for a link narrower
      // than the scrollport — so this never fights itself or overshoots.
      if (pastEnd > 0) {
        scroller.scrollLeft += pastEnd;
      } else if (pastStart > 0) {
        scroller.scrollLeft -= pastStart;
      }
    },
    // Passive: the handler never calls preventDefault, and saying so lets the browser skip waiting
    // on it before painting.
    { passive: true }
  );
})();
